"use client"

import { useState } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { Button } from "./ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { Badge } from "./ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Package, Menu, X, User, LogOut, Settings } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { useAuth } from "../contexts/AuthContext"
import LanguageSwitcher from "./LanguageSwitcher"

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useLanguage()
  const { user, isAuthenticated, logout } = useAuth()

  const handleLogout = () => {
    logout()
    navigate("/")
  }

  const isActive = (path: string) => {
    return location.pathname === path
  }

  // 获取用户头像首字母
  const getAvatarFallback = () => {
    if (user?.account) {
      return user.account.charAt(0).toUpperCase()
    }
    return "U"
  }

  return (
    <nav className="bg-white shadow-sm border-b fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 relative">
              <div className="relative">
                <Package className="h-8 w-8 text-blue-600" />
                <Badge className="absolute -top-1 -right-1 text-[8px] px-0.5 py-0 bg-orange-500 hover:bg-orange-600">
                  Beta
                </Badge>
              </div>
              <span className="text-xl font-bold text-gray-900">Craft.Jamerly.AI</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                isActive("/") ? "text-blue-600" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t('nav.home')}
            </Link>

            <Link
              to="/marketplace"
              className={`text-sm font-medium transition-colors ${
                isActive("/marketplace") ? "text-blue-600" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t('nav.marketplace')}
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to="/dashboard"
                  className={`text-sm font-medium transition-colors ${
                    isActive("/dashboard") ? "text-blue-600" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t('nav.dashboard')}
                </Link>
                <Link
                  to="/profile"
                  className={`text-sm font-medium transition-colors ${
                    isActive("/profile") ? "text-blue-600" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t('nav.profile')}
                </Link>
              </>
            )}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.avatar || undefined} alt={user?.account || "U"} />
                      <AvatarFallback>{getAvatarFallback()}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.account}</p>
                      <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>{t('nav.profile')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>{t('nav.dashboard')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('nav.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-4">
                <Button variant="ghost" onClick={() => navigate("/login")}>
                  {t('nav.login')}
                </Button>
                <Button onClick={() => navigate("/register")}>{t('nav.register')}</Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            <Link
              to="/"
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive("/") ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>

            <Link
              to="/marketplace"
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive("/marketplace") ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav.marketplace')}
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to="/dashboard"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive("/dashboard") ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:text-gray-900"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.dashboard')}
                </Link>
                <Link
                  to="/profile"
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive("/profile") ? "text-blue-600 bg-blue-50" : "text-gray-600 hover:text-gray-900"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.profile')}
                </Link>
              </>
            )}
            {!isAuthenticated && (
              <div className="pt-4 pb-3 border-t border-gray-200">
                <div className="flex items-center px-3">
                  <Button variant="ghost" className="w-full" onClick={() => navigate("/login")}>
                    {t('nav.login')}
                  </Button>
                </div>
                <div className="mt-2 px-3">
                  <Button className="w-full" onClick={() => navigate("/register")}>
                    {t('nav.register')}
                  </Button>
                </div>
              </div>
            )}
            {isAuthenticated && (
              <div className="pt-4 pb-3 border-t border-gray-200">
                <div className="px-3">
                  <Button variant="ghost" className="w-full" onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </Button>
                </div>
              </div>
            )}
            {/* Mobile Language Switcher */}
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="px-3">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
